import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { ChatActionType, type AccountSearchResult } from '@trinity/chat';
import { useSelectedUserTerritoryId } from '@trinity/chat-components/components/Chat.Filters/hooks/useSelectedUserTerritoryId';
import { SelectAccount } from '@trinity/chat-components/components/Search/Account';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { SearchQueryHint } from '../presentation/Message.QueryHint';
import { SubmitActionContext } from './context';
import { useAccountSelectionAction } from './hooks/useAccountSelectionAction';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  disabled?: boolean;
  searchAccountsQueryFn?: (params: {
    name: string;
    brandIdentifier: string | null;
    territoryId?: string;
  }) => Promise<AccountSearchResult[]>;
};

export const AccountSearchResponse = ({ disabled = false, searchAccountsQueryFn }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const submitAccountSelection = useAccountSelectionAction();
  const apiClient = useChatApiClient();
  const territoryId = useSelectedUserTerritoryId();

  const [inputValue, setInputValue] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<AccountSearchResult | null>(null);

  const queryFn = useCallback(
    async ({
      name,
      brandIdentifier,
      territoryId: selectedTerritoryId,
    }: {
      name: string;
      brandIdentifier: string | null;
      territoryId?: string;
    }) => {
      if (searchAccountsQueryFn) {
        return searchAccountsQueryFn({ name, brandIdentifier, territoryId: selectedTerritoryId });
      }

      const res = await apiClient.search.searchAccounts({
        name,
        territoryId: selectedTerritoryId,
      });

      return res.results;
    },
    [apiClient, searchAccountsQueryFn]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setSelectedAccount(null);
  }, []);

  const handleSelect = useCallback(
    (account: AccountSearchResult) => {
      if (disabled) return;

      setSelectedAccount(account);
      setInputValue(account.name);

      submitAccountSelection(account);
    },
    [disabled, submitAccountSelection]
  );

  const handleSubmit = useCallback(() => {
    if (disabled) return;

    if (selectedAccount) {
      submitAccountSelection(selectedAccount);
      return;
    }

    const trimmedName = inputValue.trim();
    if (trimmedName.length === 0) return;

    submitAction(
      {
        type: ChatActionType.SearchAccounts,
        payload: {
          name: trimmedName,
          territoryId,
        },
      },
      `Find any matching account with the name ${trimmedName}`
    );
  }, [disabled, inputValue, selectedAccount, submitAction, submitAccountSelection, territoryId]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          <IntroText>
            Get started with a search to find an account
            <br />
            First let&apos;s find the account you&apos;re looking for.
          </IntroText>

          <FormRow>
            <Label>Name</Label>
            <SelectAccount
              disabled={disabled}
              inputValue={inputValue}
              selectedAccount={selectedAccount}
              onInputChange={handleInputChange}
              onSelection={handleSelect}
              onKeyDown={handleKeyDown}
              territoryId={territoryId}
              queryFn={queryFn}
            />
          </FormRow>

          <SearchQueryHint
            disabled={disabled || inputValue.trim().length === 0}
            onClick={handleSubmit}
            value="Search for accounts."
          />
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

const Body = styled.div({
  width: '100%',
});

const IntroText = styled.div(({ theme }) => ({
  marginBottom: 16,
  fontFamily: theme.fonts.regular,
}));

const FormRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 16,
});

const Label = styled.span(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  minWidth: 50,
}));
